import { isString } from 'lodash-es'
import app from '#app/app'
import { appLayout } from '#app/init_app_layout.svelte.ts'
import { isNonEmptyString, isUserAcct, isUserId } from '#app/lib/boolean_tests'
import { newError, type ErrorContext } from '#app/lib/error'
import { getAllQuerystringParameters } from '#app/lib/querystring_helpers'
import { addRoutes } from '#app/lib/router'
import { getEpochTime, getLocalDateTime } from '#app/lib/time'
import { commands, reqres } from '#app/radio'
import type { PatchOperationFilter } from '#entities/lib/patches/operations'
import type { Group, GroupId, GroupSlug } from '#server/types/group'
import type { UserAccountUri } from '#server/types/server'
import type { UserId, User, Username, AnonymizableUserId } from '#server/types/user'
import { i18n } from '#user/lib/i18n'
import { serializedMainUser, type SerializedMainUser } from '#user/lib/serialized_main_user'
import { resolveToGroup } from '../groups/lib/groups'
import { initRelations } from './lib/relations'
import { getLocalUserAccount, type SerializedUser } from './lib/users'
import { getUserByAcct, getUserById, getUserByUsername, resolveToUser, type ExtendedUser } from './users_data'

export default {
  initialize () {
    addRoutes({
      '/u(sers)(/)': 'showNetworkHome',
      '/u(sers)/network(/)': 'showNetworkHome',
      '/u(sers)/public(/)': 'showPublicHome',
      '/u(sers)/latest(/)': 'showLatestUsers',
      '/u(sers)/:id(/)': 'showUserProfile',
      '/u(sers)/:id/followers(/)': 'showUserFollowers',
      '/u(sers)/:id/inventory/:uri(/)': 'showUserItemsByEntity',
      '/u(sers)/:id/inventory(/)': 'showUserInventory',
      '/u(sers)/:id/lists(/)': 'showUserListings',
      '/u(sers)/:id/contributions(/)': 'showUserContributionsFromRoute',
    }, controller)

    initRelations()
  },
}

export async function showHome () {
  if (reqres.request('require:loggedIn', '/')) {
    // Give focus to the home button so that hitting tab gives focus
    // to the search input
    ;(document.querySelector('#home') as HTMLElement).focus()
    return showMainUserProfile()
  }
}

export async function showUserProfile (user) {
  return showUsersHome({ user })
}

export async function showMainUserProfile () {
  return showUsersHome({ user: serializedMainUser })
}

export async function showUserInventory (user) {
  return showUsersHome({ user, profileSection: 'inventory' })
}

export async function showUserListings (user) {
  return showUsersHome({ user, profileSection: 'listings' })
}

async function showLatestUsers () {
  if (!reqres.request('require:admin:access')) return
  const { default: LatestUsers } = await import('#users/components/latest_users.svelte')
  appLayout.showChildComponent('main', LatestUsers)
  app.navigate('users/latest', { metadata: { title: i18n('Latest users') } })
}

const controller = {
  showHome,
  showNetworkHome () {
    const pathname = 'users/network'
    app.navigate('users/network')
    if (reqres.request('require:loggedIn', pathname)) {
      return showUsersHome({ section: 'network' })
    }
  },
  showPublicHome () {
    const pathname = 'users/public'
    app.navigate(pathname)
    if (reqres.request('require:loggedIn', pathname)) {
      return showUsersHome({ section: 'public' })
    }
  },
  showUserProfile,
  showUserInventory,
  showUserListings,
  showUserFollowers,
  showUserItemsByEntity (username, uri) {
    commands.execute('show:user:items:by:entity', username, uri)
  },
  showUserContributionsFromRoute (idOrUsername: string) {
    const { since, until, filter } = getAllQuerystringParameters()
    const operationFilter = isString(filter) ? filter as PatchOperationFilter : undefined
    const sinceEpoch = isNonEmptyString(since) ? getEpochTime(since) : undefined
    const untilEpoch = isNonEmptyString(until) ? getEpochTime(until) : undefined
    showUserContributionsFromUserAcctOrIdOrUsername(idOrUsername, operationFilter, sinceEpoch, untilEpoch)
  },
  showLatestUsers,
}

interface ShowUsersHome {
  user?: User | UserId | Username | UserAccountUri | SerializedUser | SerializedMainUser
  group?: Group | GroupId | GroupSlug
  section?: 'public' | 'network'
  profileSection?: 'inventory' | 'listings'
}

export async function showUsersHome ({ user, group, section, profileSection }: ShowUsersHome) {
  try {
    const { default: UsersHomeLayout } = await import('#users/components/users_home_layout.svelte')
    const props = {
      section,
      profileSection,
      user: user ? await resolveToUser(user) : undefined,
      group: group ? await resolveToGroup(group) : undefined,
    }
    appLayout.showChildComponent('main', UsersHomeLayout, { props })
  } catch (err) {
    commands.execute('show:error', err)
  }
}

async function showUserContributionsFromUserAcctOrIdOrUsername (userAcctOrIdOrUsername: string, operationFilter?: PatchOperationFilter, since?: EpochTimeStamp, until?: EpochTimeStamp) {
  try {
    // Only resolve to the user acct to let showUserContributionsFromAcct
    // resolve to the actual contributor
    const userAcct = await resolveToUserAcct(userAcctOrIdOrUsername)
    await showUserContributionsFromAcct(userAcct, operationFilter, since, until)
  } catch (err) {
    commands.execute('show:error', err)
  }
}

export async function showUserContributionsFromAcct (userAcct: UserAccountUri, operationFilter?: PatchOperationFilter, since?: EpochTimeStamp, until?: EpochTimeStamp) {
  try {
    const contributor = await getUserByAcct(userAcct)
    const { username, contributionsPathname, acct } = contributor
    let path = contributionsPathname
    const params = new URLSearchParams()
    if (operationFilter) params.append('filter', operationFilter)
    if (since) params.append('since', getLocalDateTime(since))
    if (until) params.append('until', getLocalDateTime(until))
    if (params.size > 0) path += `?${params.toString()}`
    const title = i18n('contributions_by', { username: username || acct })
    app.navigate(path, { metadata: { title } })
    const { default: Contributions } = await import('#entities/components/patches/contributions.svelte')
    appLayout.showChildComponent('main', Contributions, { props: { contributor, operationFilter, since, until } })
  } catch (err) {
    commands.execute('show:error', err)
  }
}

async function showUserFollowers (idOrUsername: UserId | Username) {
  try {
    const [
      { default: UsersHomeLayout },
      user,
    ] = await Promise.all([
      import('#users/components/users_home_layout.svelte'),
      resolveToUser(idOrUsername),
    ])
    appLayout.showChildComponent('main', UsersHomeLayout, {
      props: {
        showUserFollowers: true,
        user,
      },
    })
  } catch (err) {
    commands.execute('show:error', err)
  }
}

async function resolveToUserAcct (userAcctOrIdOrUsername: UserAccountUri | UserId | Username): Promise<UserAccountUri> {
  if (isUserAcct(userAcctOrIdOrUsername)) {
    return userAcctOrIdOrUsername
  } else if (isUserId(userAcctOrIdOrUsername)) {
    try {
      const user = await getUserById(userAcctOrIdOrUsername as UserId)
      return parseLocalUserAcct(user, { userId: userAcctOrIdOrUsername })
    } catch (err) {
      if (err.statusCode === 404) {
        return getLocalUserAccount(userAcctOrIdOrUsername as AnonymizableUserId)
      } else {
        throw err
      }
    }
  } else {
    const user = await getUserByUsername(userAcctOrIdOrUsername)
    return parseLocalUserAcct(user, { username: userAcctOrIdOrUsername })
  }
}

function parseLocalUserAcct (user: User | ExtendedUser, context: ErrorContext) {
  if (!user.anonymizableId) {
    throw newError("This user's contributions are private", 403, context)
  }
  return getLocalUserAccount(user.anonymizableId)
}
